import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { Seo } from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import api from "../lib/api";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const present = new Set(items.map((i) => i.treatment));
  const chips = [{ slug: "all", name: "All results" }, ...categories.filter((c) => present.has(c.slug))];
  const filtered = active === "all" ? items : items.filter((i) => i.treatment === active);

  return (
    <div data-testid="gallery-page">
      <Seo title="Before & After Results" description="Real before-and-after results from treatments at Iridescent Medical & Wellness Spa in Clinton Township, MI." path="/gallery" />
      <PageHeader
        overline="Real Results"
        title="Before &amp; after"
        subtitle="Real results from real clients, achieved by our expert team. Individual results vary."
      />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {items.length === 0 ? (
            <Reveal>
              <p className="text-center text-[#8A9492] py-16" data-testid="gallery-empty">
                Our results gallery is being curated — check back soon.
              </p>
            </Reveal>
          ) : (
            <>
              {chips.length > 2 && (
                <div className="flex flex-wrap gap-2 mb-10" data-testid="gallery-filters">
                  {chips.map((c) => (
                    <button
                      key={c.slug}
                      onClick={() => setActive(c.slug)}
                      data-testid={`gallery-filter-${c.slug}`}
                      className={`px-4 py-2 rounded-full text-sm transition-colors ${
                        active === c.slug ? "iri-gradient text-ink font-medium" : "border border-slate-200 text-[#5C6664] hover:border-slate-300"
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}

              <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((it) => (
                  <StaggerItem key={it.id}>
                    <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover-lift" data-testid={`gallery-item-${it.id}`}>
                      <BeforeAfterSlider before={it.before_url} after={it.after_url} alt={it.title || "Treatment result"} className="aspect-square" />
                      {it.title && <p className="text-sm text-[#5C6664] p-4">{it.title}</p>}
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </>
          )}

          <p className="text-xs text-[#8A9492] leading-relaxed mt-16 max-w-3xl mx-auto text-center" data-testid="gallery-disclaimer">
            The before and after images displayed on this website are actual patient results and are provided for
            informational and illustrative purposes only. Individual results vary significantly and depend on many
            factors, including age, skin quality, anatomy, lifestyle, medical history, and compliance with the
            recommended treatment plan and aftercare. No representation is made that any patient will achieve results
            similar to those shown. These photographs do not constitute a guarantee, warranty, or promise of specific
            results. A personal consultation is required to determine whether a treatment is appropriate and what
            results may reasonably be expected.
          </p>
        </div>
      </section>
    </div>
  );
}

function BAImage({ src, label }) {
  return (
    <div className="relative aspect-square overflow-hidden bg-stone">
      <img src={src} alt={label} className="w-full h-full object-cover" />
      <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.15em] bg-ink/80 text-white px-2 py-0.5 rounded-full">
        {label}
      </span>
    </div>
  );
}
